<script setup lang="ts">
import { nextTick, onBeforeUnmount, ref } from "vue";
import { STATUSES, type Status } from "@shared/types.js";
import { swallowPress } from "../lib/dismiss.js";

const props = defineProps<{ status: Status }>();
const emit = defineEmits<{ (e: "change", next: Status): void }>();

const open = ref(false);
const chip = ref<HTMLElement | null>(null);
const menu = ref<HTMLElement | null>(null);
const pos = ref({ top: 0, left: 0 });

/** The chips live inside scrolling lists that would clip an absolute menu, so
 *  it is teleported to <body> and pinned under the chip in viewport space —
 *  or above it, when the chip is too close to the bottom edge. */
async function place(): Promise<void> {
  await nextTick();
  if (!chip.value || !menu.value) return;
  const c = chip.value.getBoundingClientRect();
  const m = menu.value.getBoundingClientRect();
  const below = c.bottom + 3 + m.height <= window.innerHeight - 8;
  pos.value = {
    top: below ? c.bottom + 3 : Math.max(8, c.top - 3 - m.height),
    // Left-aligned with the chip, slid back in if that runs off the right edge.
    left: Math.max(8, Math.min(c.left, window.innerWidth - 8 - m.width)),
  };
}

function toggle(): void {
  open.value = !open.value;
  if (open.value) {
    void place();
    window.addEventListener("pointerdown", onDocPointerDown, true);
    // Capture, so Escape closes this menu and not a modal the chip sits in.
    window.addEventListener("keydown", onKey, true);
    // Any scroll moves the chip out from under a fixed menu; just close it.
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
  } else {
    close();
  }
}

function close(): void {
  open.value = false;
  window.removeEventListener("pointerdown", onDocPointerDown, true);
  window.removeEventListener("keydown", onKey, true);
  window.removeEventListener("scroll", close, true);
  window.removeEventListener("resize", close);
}

function onDocPointerDown(ev: PointerEvent): void {
  const t = ev.target as Node;
  if (menu.value?.contains(t) || chip.value?.contains(t)) return;
  close();
  swallowPress(ev);
}

function onKey(ev: KeyboardEvent): void {
  if (ev.key !== "Escape") return;
  ev.stopPropagation();
  close();
}

function choose(s: Status): void {
  if (s !== props.status) emit("change", s);
  close();
}

onBeforeUnmount(close);
</script>

<template>
  <button
    ref="chip"
    class="chip"
    :class="[status, { open }]"
    :title="`${status} — click to change`"
    aria-haspopup="listbox"
    :aria-expanded="open"
    @click.stop="toggle"
  >
    {{ status }}
  </button>

  <Teleport to="body">
    <ul
      v-if="open"
      ref="menu"
      class="status-menu"
      role="listbox"
      :style="{ top: pos.top + 'px', left: pos.left + 'px' }"
    >
      <li
        v-for="s in STATUSES"
        :key="s"
        role="option"
        :aria-selected="s === status"
        :class="{ current: s === status }"
        @click="choose(s)"
      >
        <span class="status-dot" :class="s"></span>{{ s }}
      </li>
    </ul>
  </Teleport>
</template>
